import { useState, useEffect, useCallback } from "react";
import { getPrestigeBonus } from "../utils";
import { ACHIEVEMENTS } from "../components/menus/Achievements";
import { setMasterVolume, startBackgroundMusic, stopBackgroundMusic } from "../Sounds";

function load(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? JSON.parse(v) : fallback;
  } catch { return fallback; }
}
function save(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

export default function useGameState() {
  // ── Core state ─────────────────────────────────────────────
  const [inventory,     setInventory]     = useState(() => load("inventory", []));
  const [coins,         setCoins]         = useState(() => load("coins", 100));
  const [totalCoins,    setTotalCoins]    = useState(() => load("totalCoins", 0));
  const [rollCount,     setRollCount]     = useState(() => load("rollCount", 0));
  const [pity,          setPity]          = useState(() => load("pity", { Rare: 0, Epic: 0, Legendary: 0 }));
  const [boosts,        setBoosts]        = useState(() => load("boosts", {}));
  const [prestige,      setPrestige]      = useState(() => load("prestige", 0));
  const [unlockedAch,   setUnlockedAch]   = useState(() => load("achievements", []));
  const [favourites,    setFavourites]    = useState(() => load("favourites", []));
  const [pullCounts,    setPullCounts]    = useState(() => load("pullCounts", {}));
  const [pullStats,     setPullStats]     = useState(() => load("pullStats", {}));
  const [rollHistory,   setRollHistory]   = useState(() => load("rollHistory", []));

  // ── Settings ───────────────────────────────────────────────
  const [theme,         setTheme]         = useState(() => load("theme", "dark"));
  const [soundEnabled,  setSoundEnabled]  = useState(() => load("soundEnabled", true));
  const [volume,        setVolume]        = useState(() => load("volume", 80));
  const [musicOn,       setMusicOn]       = useState(() => load("musicOn", false));
  const [musicVolume,   setMusicVolume]   = useState(() => load("musicVolume", 40));
  const [cutscenes,     setCutscenes]     = useState(() => load("cutscenes", { Mythic: true, Ultra: true }));
  const [wheelEnabled,  setWheelEnabled]  = useState(() => load("wheelEnabled", true));

  // ── Persist effects ────────────────────────────────────────
  useEffect(() => { save("inventory",    inventory);    }, [inventory]);
  useEffect(() => { save("coins",        coins);        }, [coins]);
  useEffect(() => { save("totalCoins",   totalCoins);   }, [totalCoins]);
  useEffect(() => { save("rollCount",    rollCount);    }, [rollCount]);
  useEffect(() => { save("pity",         pity);         }, [pity]);
  useEffect(() => { save("boosts",       boosts);       }, [boosts]);
  useEffect(() => { save("prestige",     prestige);     }, [prestige]);
  useEffect(() => { save("achievements", unlockedAch);  }, [unlockedAch]);
  useEffect(() => { save("favourites",   favourites);   }, [favourites]);
  useEffect(() => { save("pullCounts",   pullCounts);   }, [pullCounts]);
  useEffect(() => { save("pullStats",    pullStats);    }, [pullStats]);
  useEffect(() => { save("rollHistory",  rollHistory);  }, [rollHistory]);
  useEffect(() => { save("theme",        theme);        document.documentElement.setAttribute("data-theme", theme); }, [theme]);
  useEffect(() => { save("soundEnabled", soundEnabled); }, [soundEnabled]);
  useEffect(() => { save("volume",       volume);       setMasterVolume(soundEnabled ? volume / 100 : 0); }, [volume, soundEnabled]);
  useEffect(() => { save("musicOn",      musicOn);      musicOn ? startBackgroundMusic() : stopBackgroundMusic(); }, [musicOn]);
  useEffect(() => { save("musicVolume",  musicVolume);  }, [musicVolume]);
  useEffect(() => { save("cutscenes",    cutscenes);    }, [cutscenes]);
  useEffect(() => { save("wheelEnabled", wheelEnabled); }, [wheelEnabled]);

  // ── Cross-tab sync ─────────────────────────────────────────
  // When another tab writes a game key to localStorage, update
  // React state directly — no reload, no flicker.
  useEffect(() => {
    const handler = (e) => {
      if (!e.key || e.newValue === null) return;
      let val;
      try { val = JSON.parse(e.newValue); } catch { return; }
      switch (e.key) {
        case "inventory":    setInventory(val);    break;
        case "coins":        setCoins(val);        break;
        case "totalCoins":   setTotalCoins(val);   break;
        case "rollCount":    setRollCount(val);    break;
        case "pity":         setPity(val);         break;
        case "boosts":       setBoosts(val);       break;
        case "prestige":     setPrestige(val);     break;
        case "achievements": setUnlockedAch(val);  break;
        case "favourites":   setFavourites(val);   break;
        case "pullCounts":   setPullCounts(val);   break;
        case "pullStats":    setPullStats(val);    break;
        case "rollHistory":  setRollHistory(val);  break;
        case "theme":        setTheme(val);        break;
        case "soundEnabled": setSoundEnabled(val); break;
        case "volume":       setVolume(val);       break;
        case "musicOn":      setMusicOn(val);      break;
        case "musicVolume":  setMusicVolume(val);  break;
        case "cutscenes":    setCutscenes(val);    break;
        case "wheelEnabled": setWheelEnabled(val); break;
        default: break;
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  // Apply theme on mount
  useEffect(() => { document.documentElement.setAttribute("data-theme", theme); }, []); // eslint-disable-line

  // ── Achievement checker ────────────────────────────────────
  const checkAchievements = useCallback((state) => {
    const newlyUnlocked = [];
    ACHIEVEMENTS.forEach(ach => {
      if (!state.unlockedAch.includes(ach.id) && ach.check(state)) {
        newlyUnlocked.push(ach);
      }
    });
    return newlyUnlocked;
  }, []);

  // ── Computed achievement state ─────────────────────────────
  const buildAchState = useCallback((inv, rc, tc, prest, ach) => ({
    rolls:        rc,
    owned:        inv.length,
    totalCoins:   tc,
    prestige:     prest,
    names:        inv.map(i => i.name),
    rarities:     inv.map(i => i.rarity),
    streak:       parseInt(localStorage.getItem("streak") || "0", 10),
    unlockedAch:  ach,
  }), []);

  // ── Add roll result to state ───────────────────────────────
  const applyRollResult = useCallback((selected, coinEarned, isDupe, newPity) => {
    setInventory(prev => {
      if (isDupe) return prev;
      return [...prev, selected];
    });
    setCoins(prev => prev + coinEarned);
    setTotalCoins(prev => prev + coinEarned);
    setRollCount(prev => prev + 1);
    setPity(newPity);
    setPullCounts(prev => ({ ...prev, [selected.name]: (prev[selected.name] || 0) + 1 }));
    setPullStats(prev => {
      const existing = prev[selected.name];
      return {
        ...prev,
        [selected.name]: {
          count:     (existing?.count || 0) + 1,
          firstPull: existing?.firstPull || Date.now(),
        },
      };
    });
    setRollHistory(prev => {
      const entry = { name: selected.name, image: selected.image, rarity: selected.rarity, earned: coinEarned, isDupe, ts: Date.now() };
      return [entry, ...prev].slice(0, 200);
    });
  }, []);

  // ── Spend coins ────────────────────────────────────────────
  const spendCoins = useCallback((amount) => {
    setCoins(prev => Math.max(0, prev - amount));
  }, []);

  // ── Add coins (streak, missions, etc.) ────────────────────
  const addCoins = useCallback((amount) => {
    setCoins(prev => prev + amount);
    setTotalCoins(prev => prev + amount);
  }, []);

  // ── Boosts ─────────────────────────────────────────────────
  const applyBoost = useCallback((boostId) => {
    setBoosts(prev => {
      const next = { ...prev };
      if (boostId === "hot_streak")       next.hot_streak       = 5;
      else if (boostId === "coin_multiplier") next.coin_multiplier = 10;
      else if (boostId === "collection_insurance") next.collection_insurance = true;
      else if (boostId === "streak_freeze") next.streak_freeze  = true;
      else if (boostId === "hamster_magnet") next.hamster_magnet = 3;
      else next[boostId] = true;
      return next;
    });
  }, []);

  const consumeBoosts = useCallback((boostsBefore) => {
    setBoosts(prev => {
      const next = { ...prev };
      if (next.hot_streak > 0)      next.hot_streak--;
      if (next.coin_multiplier > 0) next.coin_multiplier--;
      if (next.lucky_charm)         next.lucky_charm = false;
      if (next.rare_surge)          next.rare_surge  = false;
      if (next.epic_guarantee)      next.epic_guarantee = false;
      if (next.jackpot)             next.jackpot = false;
      if (next.hamster_magnet > 0)  next.hamster_magnet--;
      return next;
    });
  }, []);

  // ── Unlock achievements ────────────────────────────────────
  const unlockAchievements = useCallback((ids) => {
    setUnlockedAch(prev => [...new Set([...prev, ...ids])]);
  }, []);

  // ── Toggle favourite ───────────────────────────────────────
  const toggleFavourite = useCallback((name) => {
    setFavourites(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  }, []);

  // ── Shatter dupes ──────────────────────────────────────────
  const shatterDupes = useCallback((hamster, dupeCount, payout) => {
    const toRemove = dupeCount;
    let removed = 0;
    setInventory(prev => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0 && removed < toRemove; i--) {
        if (next[i].name === hamster.name) { next.splice(i, 1); removed++; }
      }
      return next;
    });
    setPullCounts(prev => ({ ...prev, [hamster.name]: Math.max(1, (prev[hamster.name] || 1) - toRemove) }));
    setPullStats(prev => ({
      ...prev,
      [hamster.name]: { ...prev[hamster.name], count: Math.max(1, (prev[hamster.name]?.count || 1) - toRemove) },
    }));
    addCoins(payout);
  }, [addCoins]);

  // ── Prestige reset ─────────────────────────────────────────
  const doPrestige = useCallback(() => {
    const nextLevel = prestige + 1;
    const { startCoins } = getPrestigeBonus(nextLevel);
    setPrestige(nextLevel);
    setInventory([]);
    setCoins(startCoins);
    setRollCount(0);
    setPity({ Rare: 0, Epic: 0, Legendary: 0 });
    setBoosts({});
    setPullCounts({});
    setPullStats({});
    setRollHistory([]);
    save("prestige", nextLevel);
  }, [prestige]);

  // ── Settings helpers ───────────────────────────────────────
  const toggleTheme    = useCallback(() => setTheme(t => t === "dark" ? "light" : "dark"), []);
  const toggleSound    = useCallback(() => setSoundEnabled(v => !v), []);
  const handleVolume   = useCallback((v) => setVolume(v), []);
  const toggleMusic    = useCallback(() => setMusicOn(v => !v), []);
  const handleMusicVol = useCallback((v) => { setMusicVolume(v); }, []);
  const toggleCutscene = useCallback((id) => setCutscenes(prev => ({ ...prev, [id]: prev[id] === false ? true : false })), []);
  const toggleWheel    = useCallback(() => setWheelEnabled(v => !v), []);

  return {
    // state
    inventory, coins, totalCoins, rollCount, pity, boosts, prestige,
    unlockedAch, favourites, pullCounts, pullStats, rollHistory,
    theme, soundEnabled, volume, musicOn, musicVolume, cutscenes, wheelEnabled,
    // actions
    applyRollResult, spendCoins, addCoins, applyBoost, consumeBoosts,
    unlockAchievements, toggleFavourite, shatterDupes, doPrestige,
    checkAchievements, buildAchState,
    // settings
    toggleTheme, toggleSound, handleVolume, toggleMusic, handleMusicVol,
    toggleCutscene, toggleWheel,
    setBoosts,
  };
}